import { type JSX } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  type AutocompleteProps,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import {
  ticketPrioritiesQuery,
  TicketPriorityControlGate,
} from '@features/effector-form/controls/ticketPriority/model';
import { withControl } from '@features/effector-form/hoc/withControl';
import { getAutocompleteOnChange } from '../../lib/ get-autocomplete-on-change';
import { getAutocompleteValue } from '../../lib/get-autocomplete-value';
import type { Priority } from './types';

export const TicketPriorityControl = withControl<
  Partial<AutocompleteProps<Priority, boolean, boolean, boolean>>
>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    multiple,
  }) => {
    useGate(TicketPriorityControlGate);
    const { data, pending } = useUnit(ticketPrioritiesQuery);

    const options = Array.isArray(data) ? data : [];

    const handleChange = (
      _: unknown,
      newValue: string | Priority | (string | Priority)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          loading={pending}
          value={getAutocompleteValue(options, value, multiple)}
          options={options}
          onChange={handleChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
